import { useEffect, useState } from 'react'
import { ChevronDown, ChevronUp, ExternalLink, Music } from 'lucide-react'
import { fetchAllPlaylists, fetchMusic, fetchPlaylistDetail } from '../lib/api'
import type { LifeMusicPlaylist, SpotifyPlaylist } from '../lib/api'
import LifeModal from '../components/LifeModal'

const EXTRA_BATCH = 6

type ActivePlaylist = {
  spotifyId: string
  title: string
  description?: string
  spotifyUrl?: string | null
  coverArt?: string | null
  mood?: string
  trackCount?: number | null
}

function PlaylistCard({
  title,
  coverArt,
  mood,
  description,
  trackCount,
  onClick,
  featured = false,
  className = '',
}: {
  title: string
  coverArt?: string | null
  mood?: string
  description?: string
  trackCount?: number | null
  onClick: () => void
  featured?: boolean
  className?: string
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-xl overflow-hidden text-left group focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-400 ${featured ? 'life-card-warm' : 'life-card'} ${className}`}
    >
      {coverArt ? (
        <img
          src={coverArt}
          alt={title}
          className={`w-full object-cover group-hover:scale-105 transition-transform duration-300 ${featured ? 'aspect-[16/9]' : 'aspect-square'}`}
        />
      ) : (
        <div className={`w-full bg-amber-400/10 flex items-center justify-center ${featured ? 'aspect-[16/9]' : 'aspect-square'}`}>
          <Music size={featured ? 48 : 32} className="text-amber-300/30" />
        </div>
      )}
      <div className="p-4">
        <h3 className={`font-heading font-medium text-amber-50 truncate ${featured ? 'text-2xl' : 'text-lg'}`}>{title}</h3>
        {mood && (
          <p className="text-xs text-amber-200/60 uppercase tracking-wider mt-1">{mood}</p>
        )}
        {!mood && description && (
          <p className={`text-amber-200/60 mt-1 line-clamp-2 ${featured ? 'text-sm' : 'text-xs'}`}>{description}</p>
        )}
        {!!trackCount && (
          <p className="text-xs text-amber-200/40 mt-1">{trackCount} tracks</p>
        )}
      </div>
    </button>
  )
}

export default function PlaylistGrid() {
  const [featured, setFeatured] = useState<LifeMusicPlaylist[]>([])
  const [extra, setExtra] = useState<SpotifyPlaylist[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [visibleExtra, setVisibleExtra] = useState(0)
  const [active, setActive] = useState<ActivePlaylist | null>(null)

  useEffect(() => {
    Promise.all([fetchMusic(), fetchAllPlaylists()]).then(([staticList, allPlaylists]) => {
      const byId = new Map(allPlaylists.map(p => [p.id, p]))

      const enriched: LifeMusicPlaylist[] = staticList.map(p => {
        const sp = byId.get(p.spotifyId)
        return sp
          ? { ...p, coverArt: sp.coverArt ?? undefined, trackCount: sp.trackCount, spotifyUrl: sp.spotifyUrl ?? undefined }
          : p
      })
      setFeatured(enriched)

      const featuredIds = new Set(staticList.map(p => p.spotifyId))
      setExtra(allPlaylists.filter(p => !featuredIds.has(p.id)))
      setTotalCount(allPlaylists.length)

      const missing = enriched.filter(p => p.trackCount == null)
      if (missing.length > 0) {
        Promise.allSettled(missing.map(p => fetchPlaylistDetail(p.spotifyId))).then(results => {
          const countById = new Map<string, number>()
          results.forEach((r, i) => {
            if (r.status === 'fulfilled' && typeof r.value.trackCount === 'number') {
              countById.set(missing[i].spotifyId, r.value.trackCount)
            }
          })
          if (countById.size > 0) {
            setFeatured(prev =>
              prev.map(p =>
                countById.has(p.spotifyId) ? { ...p, trackCount: countById.get(p.spotifyId) } : p,
              ),
            )
          }
        })
      }
    }).catch(() => {
      fetchMusic().then(setFeatured)
    })
  }, [])

  const openModal = (p: ActivePlaylist) => setActive(p)

  const visibleExtraItems = extra.slice(0, visibleExtra)
  const remaining = extra.length - visibleExtra
  const nextBatch = Math.min(EXTRA_BATCH, remaining)

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {featured.map((p, i) => (
          <PlaylistCard
            key={p.id}
            title={p.title}
            coverArt={p.coverArt}
            mood={p.mood}
            description={p.description}
            trackCount={p.trackCount}
            onClick={() => openModal({
              spotifyId: p.spotifyId,
              title: p.title,
              description: p.description,
              spotifyUrl: p.spotifyUrl,
              coverArt: p.coverArt,
              mood: p.mood,
              trackCount: p.trackCount,
            })}
            featured={i === 0}
            className={i === 0 ? 'sm:col-span-2' : ''}
          />
        ))}

        {visibleExtraItems.map(p => (
          <PlaylistCard
            key={p.id}
            title={p.name}
            coverArt={p.coverArt}
            description={p.description}
            trackCount={p.trackCount}
            onClick={() => openModal({
              spotifyId: p.id,
              title: p.name,
              description: p.description,
              spotifyUrl: p.spotifyUrl,
              coverArt: p.coverArt,
              trackCount: p.trackCount,
            })}
          />
        ))}
      </div>

      {/* Gradual expand / collapse */}
      {extra.length > 0 && (
        <div className="mt-6 flex items-center justify-center gap-5">
          {visibleExtra > 0 && (
            <button
              onClick={() => setVisibleExtra(0)}
              className="inline-flex items-center gap-1.5 text-sm text-amber-300/50 hover:text-amber-300 transition-colors"
            >
              <ChevronUp size={14} />
              Show less
            </button>
          )}
          {remaining > 0 && (
            <button
              onClick={() => setVisibleExtra(v => v + EXTRA_BATCH)}
              className="inline-flex items-center gap-1.5 text-sm text-amber-300 hover:text-amber-100 transition-colors"
            >
              <ChevronDown size={14} />
              {visibleExtra === 0
                ? `Show more · ${extra.length} playlists`
                : `${nextBatch} more`}
            </button>
          )}
          {visibleExtra > 0 && (
            <span className="text-xs text-amber-200/30">
              {featured.length + visibleExtra} of {totalCount}
            </span>
          )}
        </div>
      )}

      <LifeModal
        isOpen={active !== null}
        onClose={() => setActive(null)}
        title={active?.title ?? ''}
        size="xl"
      >
        {active && (
          <div>
            {/* Cover art banner — flush against modal header */}
            {active.coverArt && (
              <div className="w-full overflow-hidden" style={{ maxHeight: 220 }}>
                <img
                  src={active.coverArt}
                  alt={active.title}
                  className="w-full object-cover"
                  style={{ aspectRatio: '2/1', objectPosition: 'center' }}
                />
              </div>
            )}

            <div className="p-6 space-y-4">
              {/* Mood + track count */}
              {(active.mood || active.trackCount != null) && (
                <div className="flex items-center gap-3 flex-wrap">
                  {active.mood && (
                    <span className="text-xs font-mono text-amber-400 uppercase tracking-wider">
                      {active.mood}
                    </span>
                  )}
                  {active.trackCount != null && (
                    <span className="text-xs text-amber-200/50">{active.trackCount} tracks</span>
                  )}
                </div>
              )}

              {active.description && (
                <p className="text-amber-100/70 leading-relaxed">{active.description}</p>
              )}

              {/* Spotify embed */}
              <div className="rounded-xl overflow-hidden shadow-xl">
                <iframe
                  src={`https://open.spotify.com/embed/playlist/${active.spotifyId}?utm_source=generator`}
                  width="100%"
                  height="380"
                  frameBorder="0"
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                  title={active.title}
                />
              </div>

              <a
                href={active.spotifyUrl ?? `https://open.spotify.com/playlist/${active.spotifyId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-amber-300 hover:text-amber-100 transition-colors text-sm"
              >
                <ExternalLink size={14} />
                Open in Spotify
              </a>
            </div>
          </div>
        )}
      </LifeModal>
    </div>
  )
}
